import { Injectable } from '@angular/core';
import { TagsApi } from '@app/tags/tag-api.model';
import { TagsService } from '@app/tags/services/tags.service';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { pascalCaseKeys } from '@app/shared/utils/property';
import { AuthService } from '@app/shared/services/auth.service';
import { OnboardingService } from './onboarding.service';
import { TranslateService } from '@ngx-translate/core';
import { OnboardingStateService } from './onboarding-state.service';
import { Skill } from '../onboarding.model';
import { tap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ReviewSkillsService {
  public i18n = this.translateService.instant([
    'Onboarding_ProfileSkill',
    'Onboarding_AddedSkill',
    'Onboarding_RoleSkill',
    'Onboarding_ResumeSkill',
  ]);

  constructor(
    private tagsService: TagsService,
    private authService: AuthService,
    private onboardingService: OnboardingService,
    private translateService: TranslateService,
    private onboardingStateService: OnboardingStateService
  ) {}

  /**
   * initiate a list of tags added during onboarding and compare with user skills
   * (onboarding added skills role, and resume)
   */
  public createOnboardingAddedTags(): void {
    // get list of skills from user role assigned, and resume and create a onboardingUserAddedTags

    combineLatest([
      this.onboardingService.getUserTags(), // userProfileTags
      this.onboardingStateService.assignedTags$, // roleSkills
      this.onboardingStateService.currentResume$, // resume
    ])
      .pipe(
        tap(([userProfileTags, roleSkills, currentResume]) => {
          // map all skills to have a source type
          const roleSkillsUpdate: Skill[] = roleSkills
            ?.filter((role) => role.isFollowing)
            .map((obj) => ({
              ...obj,
              ratings: obj.ratings ? obj.ratings : [],
              source: this.i18n.Onboarding_RoleSkill,
            }));

          const resumeSkillsUpdate: Skill[] = currentResume?.skills.map(
            (obj) => ({
              ...obj,
              ratings: obj.ratings ? obj.ratings : [],
              source: this.i18n.Onboarding_ResumeSkill,
              tagId: obj.tagId
                ? obj.tagId
                : (obj as any).id
                ? (obj as any).id
                : undefined,
            })
          );

          let onboardingAddedSkills = [];
          const userTagIds = new Set(userProfileTags.map((d) => d.tagId));
          if (roleSkillsUpdate) {
            const onboardingAddedSkillsIds = new Set(
              onboardingAddedSkills.map((d) => d.tagId)
            );
            onboardingAddedSkills = [
              ...onboardingAddedSkills,
              ...roleSkillsUpdate?.filter(
                (d) =>
                  !onboardingAddedSkillsIds.has(d.tagId) &&
                  !userTagIds.has(d.tagId)
              ),
            ];
          }

          if (resumeSkillsUpdate) {
            const onboardingAddedSkillsIds = new Set(
              onboardingAddedSkills.map((d) => d.tagId)
            );
            onboardingAddedSkills = [
              ...onboardingAddedSkills,
              ...resumeSkillsUpdate?.filter(
                (d) =>
                  !onboardingAddedSkillsIds.has(d.tagId) &&
                  !userTagIds.has(d.tagId)
              ),
            ];
          }
          this.onboardingStateService.onboardingUserAddedTagsChange(
            onboardingAddedSkills
          );
        })
      )
      .subscribe();
  }

  // Submit a self rating for a tag
  public selfRating(tag: Partial<TagsApi.Tag>, level: number) {
    // If the tag doesn't have a tagId we need to create the tag.
    if (!tag.tagId) {
      this.tagsService.createTags([tag.name]).subscribe((tags) => {
        const tagId = tags[0].tagId;

        // update skill & tag with the new tag id
        tag.tagId = tagId;

        const skills = (this.onboardingStateService.addedTags || []).map(
          (skill) => {
            if (skill.name === tag.name && !skill.tagId) {
              skill.tagId = tagId;
            }
            return skill;
          }
        );
        this.onboardingStateService.addSkillsChange(skills);

        this.addSelfRating(tag, level);
      });
    } else {
      this.addSelfRating(tag, level);
    }
  }

  private addSelfRating(tag: Partial<TagsApi.Tag>, level: number) {
    this.tagsService
      .rateTag(
        level as unknown as number,
        InternalTagRatingTypes.self,
        tag as TagsApi.TagDetails,
        undefined,
        event?.target as HTMLElement
      )
      .subscribe((info) => {
        // create a new ratingInfo, if it is not a profile skill that is already added the rate service does not update this tag
        if (tag.source !== this.i18n.Onboarding_ProfileSkill) {
          const selfIndex = tag.ratings?.findIndex(
            (rating) => rating.type === InternalTagRatingTypes.self
          );
          const newRatingInfo: TagsApi.UserTagRating = {
            userTagRatingId: info.userTagRatingId,
            userProfileKey:
              this.authService.authUser.viewerProfile.userProfileKey,
            tagId: tag.tagId,
            raterProfileKey:
              +this.authService.authUser.viewerProfile.userProfileId,
            level: level.toString(),
            type: InternalTagRatingTypes.self,
            privacyId: 0,
            providerName: 'Degreed',
            isInternal: true,
          };
          // update rating tag info
          if (selfIndex < 0) {
            tag.ratings.push(newRatingInfo);
          } else {
            tag.ratings[selfIndex] = newRatingInfo;
          }
          // check source of that and update
          if (tag.source === this.i18n.Onboarding_AddedSkill) {
            this.onboardingService.updateAddedSkill(tag as TagsApi.Tag);
          } else {
            this.onboardingService.updateOnboardingUserAddedTagsSkill(
              tag as TagsApi.Tag
            );
          }
        } else {
          this.tagsService.notifyUserTagsModified();
        }
      });
  }
}
